import { useState, useMemo, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Button,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@openzeppelin/ui-components";
import { DdfAuctionsBrowse } from "../components/DdfAuctionsBrowse";
import { Icon } from "@ascend/shared/components";
import {
  DataTable,
  type DataTableColumn,
  PaginationControls,
  SearchBar,
  TABLE_PAGE_SIZE_OPTIONS,
  type SortDirection,
} from "../components/ListControls";
import { useWalletStore } from "../stores/useWalletStore";
import {
  CLIENT_DDF_AUCTIONS,
  CLIENT_LIQUIDATION_HISTORY_EVENTS,
  CLIENT_LIQUIDATION_POSITIONS,
  CLIENT_FACILITIES,
} from "../data/mockAdapter";

// ─── Types ──────────────────────────────────────────────────────────────────────

type SubTab = "searcher" | "verified" | "ddf" | "history";
type PositionSortKey =
  | "accountId"
  | "facility"
  | "token"
  | "collateralAmount"
  | "collateralValue"
  | "outstandingDebt"
  | "healthFactor"
  | "timeInDefaultMinutes"
  | "findersFee"
  | "profitAmount"
  | "profitMargin";
type TokenFilter = "all" | string;
type HistoryStatus = "Settled" | "Pending";
type HistoryType = "Permissionless" | "DDF";
type HistorySortKey =
  | "date"
  | "type"
  | "accountId"
  | "token"
  | "collateralAmount"
  | "debtCovered"
  | "recoveryRate"
  | "status";

interface LiquidationPosition {
  id: string;
  accountId: string;
  facility: string;
  token: string;
  tokenColor: string;
  collateralAmount: number;
  collateralValue: number;
  outstandingDebt: number;
  healthFactor: number;
  timeInDefault: string;
  timeInDefaultMinutes: number;
  profitable: boolean;
  profitAmount: number;
  profitMargin: number;
  findersFee: number;
  feeGuaranteed: boolean;
}

interface HistoryEvent {
  id: string;
  date: string;
  type: HistoryType;
  accountId: string;
  token: string;
  tokenColor: string;
  collateralAmount: number;
  debtCovered: number;
  recoveryRate: number;
  status: HistoryStatus;
}

const POSITIONS: LiquidationPosition[] = CLIENT_LIQUIDATION_POSITIONS;

const HISTORY_EVENTS: HistoryEvent[] = CLIENT_LIQUIDATION_HISTORY_EVENTS.map(
  (event) => ({
    id: event.id,
    date: event.date,
    type: event.type,
    accountId: event.accountId,
    token: event.token,
    tokenColor: event.tokenColor,
    collateralAmount: event.collateralAmount,
    debtCovered: event.debtCovered,
    recoveryRate: event.recoveryRate,
    status: event.status,
  }),
);

const ALL_TOKENS = [
  ...new Set(POSITIONS.map((position) => position.token)),
].sort();

/** Map facility display names to credit facility detail routes (borrower compliance lives there). */
const FACILITY_NAME_TO_CREDIT_FACILITY_ID: Record<string, string> =
  Object.fromEntries(CLIENT_FACILITIES.map((facility) => [facility.name, facility.id]));

const INPUT_CLASS =
  "w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2";

const VALID_LIQUIDATIONS_TABS: SubTab[] = [
  "searcher",
  "verified",
  "ddf",
  "history",
];

function useLiquidationsTabs() {
  const [searchParams, setSearchParams] = useSearchParams();
  // Derive activeTab from URL — single source of truth.
  const tabParam = searchParams.get("tab");
  const activeTab: SubTab = tabParam === "verified"
    ? "searcher"
    : VALID_LIQUIDATIONS_TABS.includes(tabParam as SubTab)
      ? (tabParam as SubTab)
      : "searcher";

  // One-time migration: rewrite legacy "verified" tab value to "searcher".
  useEffect(() => {
    if (searchParams.get("tab") !== "verified") return;
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        p.set("tab", "searcher");
        return p;
      },
      { replace: true },
    );
  }, [searchParams, setSearchParams]);

  function setActiveTab(key: SubTab) {
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        p.set("tab", key);
        return p;
      },
      { replace: true },
    );
  }

  return { activeTab, setActiveTab };
}

// ─── Helpers ────────────────────────────────────────────────────────────────────

function fmtCurrency(value: number): string {
  return `$${Math.abs(value).toLocaleString()}`;
}

function parseDateToTimestamp(value: string): number {
  const parsed = Date.parse(value);
  if (!Number.isNaN(parsed)) return parsed;
  const [day, month, year] = value.split("/").map((part) => Number(part));
  if (!day || !month || !year) return 0;
  return new Date(year, month - 1, day).getTime();
}

function compareText(a: string, b: string): number {
  return a.localeCompare(b);
}

// ─── Shared Sub-components ──────────────────────────────────────────────────────

function StatCard({
  title,
  value,
  icon,
  valueClassName,
}: {
  title: string;
  value: string;
  icon: React.ReactNode;
  valueClassName?: string;
}) {
  return (
    <div className="rounded-xl border border-border p-5 sm:p-6">
      <div className="flex items-center justify-between">
        <span className="text-xs sm:text-sm text-muted-foreground">
          {title}
        </span>
        {icon}
      </div>
      <div
        className={`mt-2 text-2xl sm:text-3xl font-bold ${valueClassName ?? ""}`}
      >
        {value}
      </div>
    </div>
  );
}

function TokenBadge({
  token,
  colorClass,
}: {
  token: string;
  colorClass: string;
}) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${colorClass}`}
    >
      {token}
    </span>
  );
}

function HealthFactorBadge({ hf }: { hf: number }) {
  const cls =
    hf < 1.0
      ? "text-red-600 font-bold"
      : hf < 1.15
        ? "text-orange-500 font-semibold"
        : "text-amber-600 font-semibold";
  return <span className={`text-xs ${cls}`}>{hf.toFixed(3)}</span>;
}

function FindersFeeCell({
  fee,
  guaranteed,
}: {
  fee: number;
  guaranteed: boolean;
}) {
  return (
    <div className="text-right">
      <div className="text-xs font-medium">{fmtCurrency(fee)}</div>
      <div
        className={`text-[10px] ${guaranteed ? "text-green-600" : "text-amber-600"}`}
      >
        {guaranteed ? "Guaranteed" : "Not guaranteed"}
      </div>
    </div>
  );
}

// ─── Permissionless Table (shared between auth/public) ──────────────────────────

function FacilityNameCell({
  facility,
  variant,
}: {
  facility: string;
  variant: "searcher" | "verified";
}) {
  const navigate = useNavigate();
  const facilityId = FACILITY_NAME_TO_CREDIT_FACILITY_ID[facility];

  if (variant === "verified" && facilityId) {
    return (
      <button
        type="button"
        onClick={() => navigate(`/credit-facilities/${facilityId}`)}
        className="text-xs truncate text-left text-primary hover:underline font-medium"
        title="Open credit facility - check compliance"
      >
        {facility}
      </button>
    );
  }

  return (
    <span className="text-xs truncate" title={facility}>
      {facility}
    </span>
  );
}

function SurplusEstCell({ pos }: { pos: LiquidationPosition }) {
  return (
    <div className="text-right">
      <div
        className={`text-xs font-medium ${pos.profitable ? "text-green-600" : "text-muted-foreground"}`}
      >
        {fmtCurrency(pos.profitAmount)}
      </div>
      <div className="text-[10px] text-muted-foreground">after debt</div>
    </div>
  );
}

function PermissionlessTable({
  positions,
  onLiquidate,
  actionLabel,
  actionDisabled,
  variant = "searcher",
  sortKey,
  sortDirection,
  onSort,
}: {
  positions: LiquidationPosition[];
  onLiquidate: (pos: LiquidationPosition) => void;
  actionLabel: string;
  actionDisabled?: boolean;
  variant?: "searcher" | "verified";
  sortKey: PositionSortKey;
  sortDirection: SortDirection;
  onSort: (nextKey: PositionSortKey) => void;
}) {
  const feeColumnLabel =
    variant === "verified" ? "Est. surplus" : "Finder's Fee";
  const feeSortKey: PositionSortKey =
    variant === "verified" ? "profitAmount" : "findersFee";
  const columns: DataTableColumn<LiquidationPosition, PositionSortKey>[] = [
    {
      id: "accountId",
      label: "Account",
      sortable: true,
      sortKey: "accountId",
      renderCell: (pos) => <span className="font-mono text-xs font-medium">{pos.accountId}</span>,
    },
    {
      id: "facility",
      label: "Facility",
      sortable: true,
      sortKey: "facility",
      renderCell: (pos) => <FacilityNameCell facility={pos.facility} variant={variant} />,
    },
    {
      id: "collateralAmount",
      label: "Collateral",
      sortable: true,
      sortKey: "collateralAmount",
      renderCell: (pos) => (
        <div className="flex items-center gap-1.5">
          <TokenBadge token={pos.token} colorClass={pos.tokenColor} />
          <span className="text-xs">{pos.collateralAmount.toFixed(2)}</span>
        </div>
      ),
    },
    {
      id: "collateralValue",
      label: "Value",
      align: "right",
      sortable: true,
      sortKey: "collateralValue",
      renderCell: (pos) => <span className="text-xs">{fmtCurrency(pos.collateralValue)}</span>,
    },
    {
      id: "outstandingDebt",
      label: "Debt",
      align: "right",
      sortable: true,
      sortKey: "outstandingDebt",
      renderCell: (pos) => <span className="text-xs">{fmtCurrency(pos.outstandingDebt)}</span>,
    },
    {
      id: "healthFactor",
      label: "HF",
      align: "center",
      sortable: true,
      sortKey: "healthFactor",
      renderCell: (pos) => <HealthFactorBadge hf={pos.healthFactor} />,
    },
    {
      id: "timeInDefaultMinutes",
      label: "Default",
      align: "center",
      sortable: true,
      sortKey: "timeInDefaultMinutes",
      renderCell: (pos) => <span className="text-xs text-muted-foreground">{pos.timeInDefault}</span>,
    },
    {
      id: "fee",
      label: feeColumnLabel,
      align: "right",
      sortable: true,
      sortKey: feeSortKey,
      renderCell: (pos) => (
        variant === "verified" ? <SurplusEstCell pos={pos} /> : <FindersFeeCell fee={pos.findersFee} guaranteed={pos.feeGuaranteed} />
      ),
    },
    {
      id: "profitMargin",
      label: "Margin",
      align: "right",
      sortable: true,
      sortKey: "profitMargin",
      renderCell: (pos) => (
        pos.profitable ? (
          <span className="text-xs font-semibold text-green-600 inline-flex items-center justify-end gap-0.5">
            <Icon name="streamline:interface-arrows-corner-up-right-keyboard-top-arrow-right-up" className="h-3 w-3" />
            {pos.profitMargin.toFixed(1)}%
          </span>
        ) : (
          <span className="rounded-full bg-red-100 text-red-700 px-2 py-0.5 text-[10px] font-medium">Unprofitable</span>
        )
      ),
    },
    {
      id: "action",
      label: "Action",
      align: "center",
      renderCell: (pos) => (
        <div className="flex justify-center">
          <Button
            variant={pos.profitable ? "default" : "outline"}
            disabled={actionDisabled}
            onClick={(e) => {
              e.stopPropagation();
              onLiquidate(pos);
            }}
          >
            {actionLabel}
          </Button>
        </div>
      ),
    },
  ];

  return (
    <>
      {/* Desktop table */}
      <div className="hidden lg:block">
        <DataTable<LiquidationPosition, PositionSortKey>
          columns={columns}
          rows={positions}
          rowKey={(pos) => pos.id}
          sort={{
            key: sortKey,
            direction: sortDirection,
            onSort,
          }}
          emptyMessage="No liquidation opportunities match your filters."
        />
      </div>

      {/* Mobile stacked cards */}
      <div className="lg:hidden space-y-3">
        {positions.map((pos) => (
          <div
            key={pos.id}
            className="rounded-lg border border-border p-4 space-y-3"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="font-mono text-sm font-medium">
                  {pos.accountId}
                </span>
                <TokenBadge token={pos.token} colorClass={pos.tokenColor} />
              </div>
              <HealthFactorBadge hf={pos.healthFactor} />
            </div>
            <div className="text-xs text-muted-foreground">
              {variant === "verified" &&
              FACILITY_NAME_TO_CREDIT_FACILITY_ID[pos.facility] ? (
                <FacilityNameCell facility={pos.facility} variant="verified" />
              ) : (
                pos.facility
              )}
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div>
                <span className="text-muted-foreground">Collateral</span>
                <div className="font-medium">
                  {pos.collateralAmount.toFixed(2)} {pos.token} (
                  {fmtCurrency(pos.collateralValue)})
                </div>
              </div>
              <div className="text-right">
                <span className="text-muted-foreground">Debt</span>
                <div className="font-medium">
                  {fmtCurrency(pos.outstandingDebt)}
                </div>
              </div>
              <div>
                <span className="text-muted-foreground">{feeColumnLabel}</span>
                {variant === "verified" ? (
                  <div
                    className={`font-medium ${pos.profitable ? "text-green-600" : "text-muted-foreground"}`}
                  >
                    {fmtCurrency(pos.profitAmount)}
                  </div>
                ) : (
                  <>
                    <div className="font-medium">
                      {fmtCurrency(pos.findersFee)}
                    </div>
                    <div
                      className={`text-[10px] ${pos.feeGuaranteed ? "text-green-600" : "text-amber-600"}`}
                    >
                      {pos.feeGuaranteed ? "Guaranteed" : "Not guaranteed"}
                    </div>
                  </>
                )}
              </div>
              <div className="text-right">
                <span className="text-muted-foreground">Margin</span>
                {pos.profitable ? (
                  <div className="font-semibold text-green-600">
                    {pos.profitMargin.toFixed(1)}%
                  </div>
                ) : (
                  <div className="font-semibold text-red-600">Unprofitable</div>
                )}
              </div>
            </div>
            <div className="flex items-center justify-between pt-1">
              <span className="text-xs text-muted-foreground">
                Default: {pos.timeInDefault}
              </span>
              <Button
                variant={pos.profitable ? "default" : "outline"}
                disabled={actionDisabled}
                onClick={() => onLiquidate(pos)}
              >
                {actionLabel}
              </Button>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

// ─── Authenticated View ─────────────────────────────────────────────────────────

function AuthenticatedLiquidations() {
  const navigate = useNavigate();
  const { activeTab, setActiveTab } = useLiquidationsTabs();
  const [sortKey, setSortKey] = useState<PositionSortKey>("profitMargin");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [tokenFilter, setTokenFilter] = useState<TokenFilter>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [historySortKey, setHistorySortKey] = useState<HistorySortKey>("date");
  const [historySortDirection, setHistorySortDirection] =
    useState<SortDirection>("desc");

  const [permPage, setPermPage] = useState(1);
  const [permPageSize, setPermPageSize] = useState<number>(
    TABLE_PAGE_SIZE_OPTIONS[0],
  );
  const [histPage, setHistPage] = useState(1);
  const [histPageSize, setHistPageSize] = useState<number>(
    TABLE_PAGE_SIZE_OPTIONS[0],
  );

  // Reset to page 1 when filters/sort change.
  const permFilterKey = `${searchQuery}|${tokenFilter}|${sortKey}|${sortDirection}`;
  const [prevPermFilterKey, setPrevPermFilterKey] = useState(permFilterKey);
  if (prevPermFilterKey !== permFilterKey) {
    setPrevPermFilterKey(permFilterKey);
    setPermPage(1);
  }
  const histFilterKey = `${historySortKey}|${historySortDirection}`;
  const [prevHistFilterKey, setPrevHistFilterKey] = useState(histFilterKey);
  if (prevHistFilterKey !== histFilterKey) {
    setPrevHistFilterKey(histFilterKey);
    setHistPage(1);
  }

  const filteredPositions = useMemo(() => {
    let items = [...POSITIONS];
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      items = items.filter(
        (p) =>
          p.accountId.toLowerCase().includes(q) ||
          p.token.toLowerCase().includes(q) ||
          p.facility.toLowerCase().includes(q),
      );
    }
    if (tokenFilter !== "all")
      items = items.filter((p) => p.token === tokenFilter);
    items.sort((a, b) => {
      let comparison = 0;
      switch (sortKey) {
        case "accountId":
          comparison = compareText(a.accountId, b.accountId);
          break;
        case "facility":
          comparison = compareText(a.facility, b.facility);
          break;
        case "token":
          comparison = compareText(a.token, b.token);
          break;
        case "collateralAmount":
          comparison = a.collateralAmount - b.collateralAmount;
          break;
        case "collateralValue":
          comparison = a.collateralValue - b.collateralValue;
          break;
        case "outstandingDebt":
          comparison = a.outstandingDebt - b.outstandingDebt;
          break;
        case "healthFactor":
          comparison = a.healthFactor - b.healthFactor;
          break;
        case "timeInDefaultMinutes":
          comparison = a.timeInDefaultMinutes - b.timeInDefaultMinutes;
          break;
        case "findersFee":
          comparison = a.findersFee - b.findersFee;
          break;
        case "profitAmount":
          comparison = a.profitAmount - b.profitAmount;
          break;
        case "profitMargin":
          comparison = a.profitMargin - b.profitMargin;
          break;
      }
      return sortDirection === "asc" ? comparison : -comparison;
    });
    return items;
  }, [sortKey, sortDirection, tokenFilter, searchQuery]);

  const paginatedPositions = useMemo(() => {
    const start = (permPage - 1) * permPageSize;
    return filteredPositions.slice(start, start + permPageSize);
  }, [filteredPositions, permPage, permPageSize]);

  const sortedHistory = useMemo(() => {
    const items = [...HISTORY_EVENTS];
    items.sort((a, b) => {
      let comparison = 0;
      switch (historySortKey) {
        case "date":
          comparison =
            parseDateToTimestamp(a.date) - parseDateToTimestamp(b.date);
          break;
        case "type":
          comparison = compareText(a.type, b.type);
          break;
        case "accountId":
          comparison = compareText(a.accountId, b.accountId);
          break;
        case "token":
          comparison = compareText(a.token, b.token);
          break;
        case "collateralAmount":
          comparison = a.collateralAmount - b.collateralAmount;
          break;
        case "debtCovered":
          comparison = a.debtCovered - b.debtCovered;
          break;
        case "recoveryRate":
          comparison = a.recoveryRate - b.recoveryRate;
          break;
        case "status":
          comparison = compareText(a.status, b.status);
          break;
      }
      return historySortDirection === "asc" ? comparison : -comparison;
    });
    return items;
  }, [historySortDirection, historySortKey]);

  const paginatedHistory = useMemo(() => {
    const start = (histPage - 1) * histPageSize;
    return sortedHistory.slice(start, start + histPageSize);
  }, [histPage, histPageSize, sortedHistory]);

  function onSort(nextKey: PositionSortKey) {
    setSortDirection((prev) =>
      sortKey === nextKey ? (prev === "asc" ? "desc" : "asc") : "desc",
    );
    setSortKey(nextKey);
  }

  function onSortHistory(nextKey: HistorySortKey) {
    setHistorySortDirection((prev) =>
      historySortKey === nextKey ? (prev === "asc" ? "desc" : "asc") : "desc",
    );
    setHistorySortKey(nextKey);
  }

  const profitableCount = POSITIONS.filter((p) => p.profitable).length;
  const totalCollateralValue = POSITIONS.reduce(
    (s, p) => s + p.collateralValue,
    0,
  );
  const potentialProfit = POSITIONS.filter((p) => p.profitable).reduce(
    (s, p) => s + p.profitAmount,
    0,
  );

  const tabs: { key: SubTab; label: string; badge?: string }[] = [
    {
      key: "searcher",
      label: "Searcher Opportunities",
      badge: String(POSITIONS.length),
    },
    { key: "verified", label: "Verified Buyer Liquidations" },
    {
      key: "ddf",
      label: "DDF Auctions",
      badge: String(CLIENT_DDF_AUCTIONS.length),
    },
    { key: "history", label: "History" },
  ];

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Page header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3">
          <Icon name="streamline:legal-justice-hammer-hammer-work-legal-mallet-office-company-gavel-justice-judge-arbitration-court" className="mt-1 h-6 w-6 sm:h-7 sm:w-7 text-foreground flex-shrink-0" />
          <div>
            <h1 className="text-xl sm:text-2xl font-bold">
              Liquidation Opportunities
            </h1>
            <p className="text-sm text-muted-foreground">
              Searchers earn bounties via the DDF; verified buyers liquidate
              directly per facility when compliance passes. Bid in DDF auctions
              for distressed collateral.
            </p>
          </div>
        </div>
        <div className="grid w-full grid-cols-1 gap-2 sm:w-auto sm:grid-cols-2">
          <Button
            className="w-full justify-center"
            variant="outline"
            size="sm"
            onClick={() => navigate("/liquidations/history")}
          >
            <Icon name="streamline:interface-time-clock-circle-clock-loading-measure-time-circle" className="mr-2 h-4 w-4" />
            History
          </Button>
          <Button
            className="w-full justify-center"
            variant="outline"
            size="sm"
            title="Verified buyers: claim collateral after winning a DDF auction or direct facility liquidation. Not for searcher bounties."
            onClick={() => navigate("/liquidations/claim/settlement")}
          >
            <Icon name="streamline:money-cash-dollar-coin-accounting-billing-payment-cash-coin-currency-money-finance" className="mr-2 h-4 w-4" />
            Settlements
          </Button>
        </div>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-1 flex-wrap">
        {tabs.map((tab) =>
          tab.key === "verified" ? (
            <TooltipProvider key={tab.key}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    className="inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors bg-muted/50 text-muted-foreground opacity-50 cursor-not-allowed"
                    disabled
                  >
                    {tab.label}
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>This feature is supported at the protocol level but is not currently enabled on the platform.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                activeTab === tab.key
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted/50 text-muted-foreground hover:text-foreground hover:bg-muted"
              }`}
            >
              {tab.label}
              {tab.badge && (
                <span
                  className={`inline-flex items-center justify-center rounded-full px-2 py-0.5 text-xs font-semibold ${
                    activeTab === tab.key
                      ? "bg-primary-foreground/20 text-primary-foreground"
                      : "bg-primary/10 text-primary"
                  }`}
                >
                  {tab.badge}
                </span>
              )}
            </button>
          ),
        )}
      </div>

      {/* ─── Searcher Opportunities Tab ─────────────────────────────── */}
      {activeTab === "searcher" && (
        <>
          {/* Summary */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4 lg:gap-4">
            <StatCard
              title="Total Positions"
              value={String(POSITIONS.length)}
              icon={<Icon name="streamline:target" className="h-5 w-5 text-muted-foreground" />}
            />
            <StatCard
              title="Profitable"
              value={String(profitableCount)}
              icon={<Icon name="streamline:graph-bar-increase" className="h-5 w-5 text-green-500" />}
              valueClassName="text-green-600"
            />
            <StatCard
              title="Total Collateral Value"
              value={fmtCurrency(totalCollateralValue)}
              icon={<Icon name="streamline:money-safe-vault-saving-combo-payment-safe-money-combination-finance" className="h-5 w-5 text-muted-foreground" />}
            />
            <StatCard
              title="Potential Bounties"
              value={fmtCurrency(potentialProfit)}
              icon={<Icon name="streamline:heart-rate-pulse-graph" className="h-5 w-5 text-green-500" />}
              valueClassName="text-green-600"
            />
          </div>

          {/* Info banner */}
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 flex items-start gap-3">
            <Icon name="streamline:interface-alert-information-circle-information-frame-info-more-help-point-circle" className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-amber-800">
              <span className="font-medium">Searcher opportunities</span> let
              anyone trigger undercollateralized positions through the DDF
              contract and earn a bounty (finder&apos;s fee) for helping maintain
              protocol health. The fee depends on the facility&apos;s
              configuration and is{" "}
              <span className="font-medium">not always guaranteed</span>: check
              the &quot;Finder&apos;s Fee&quot; column before proceeding.
              Note: while triggering is permissionless, only{" "}
              <span className="font-medium">
                compliant holders
              </span>{" "}
              can participate in auction settlement, as determined by each
              facility&apos;s compliance module.
            </div>
          </div>

          {/* Filters */}
          <Card>
            <CardContent className="p-4 sm:p-6">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-2">
                  <label className="text-xs font-medium text-muted-foreground whitespace-nowrap">
                    Token
                  </label>
                  <select
                    value={tokenFilter}
                    onChange={(e) => setTokenFilter(e.target.value)}
                    className={`${INPUT_CLASS} w-full sm:w-40`}
                  >
                    <option value="all">All Tokens</option>
                    {ALL_TOKENS.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center gap-3">
                  <SearchBar
                    value={searchQuery}
                    onChange={setSearchQuery}
                    placeholder="Search account, token, facility..."
                    className="w-full sm:w-64"
                  />
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    {filteredPositions.length} result
                    {filteredPositions.length !== 1 ? "s" : ""}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {filteredPositions.length === 0 ? (
            <div className="text-center py-12 text-sm text-muted-foreground">
              No liquidation opportunities match your filters.
            </div>
          ) : (
            <PermissionlessTable
              positions={paginatedPositions}
              onLiquidate={(pos) => navigate(`/liquidations/${pos.accountId}`)}
              actionLabel="Liquidate"
              sortKey={sortKey}
              sortDirection={sortDirection}
              onSort={onSort}
            />
          )}

          <PaginationControls
            page={permPage}
            setPage={setPermPage}
            pageSize={permPageSize}
            setPageSize={setPermPageSize}
            totalItems={filteredPositions.length}
            label="positions"
            pageSizeOptions={TABLE_PAGE_SIZE_OPTIONS}
          />
        </>
      )}

      {/* ─── Verified Buyer Liquidations Tab ────────────────────────────── */}
      {activeTab === "verified" && (
        <div className="space-y-6">
          <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 flex items-start gap-3">
            <Icon name="streamline:interface-alert-information-circle-information-frame-info-more-help-point-circle" className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-blue-800">
              <span className="font-medium">Verified buyer</span> status is{" "}
              <span className="font-medium">per credit facility</span>. Each
              facility defines its own compliance (the same checks you see when
              borrowing: KYC, OnchainID, required claims, policy). When those
              checks pass for a facility, you can liquidate margin accounts{" "}
              <span className="font-medium">in that facility only</span>,
              <span className="font-medium"> directly</span> - this path{" "}
              <span className="font-medium">bypasses DDF auctions</span>.
            </div>
          </div>

          {/* Filters (shared data + state with Searcher tab) */}
          <Card>
            <CardContent className="p-4 sm:p-6">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-2">
                  <label className="text-xs font-medium text-muted-foreground whitespace-nowrap">
                    Token
                  </label>
                  <select
                    value={tokenFilter}
                    onChange={(e) => setTokenFilter(e.target.value)}
                    className={`${INPUT_CLASS} w-full sm:w-40`}
                  >
                    <option value="all">All Tokens</option>
                    {ALL_TOKENS.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center gap-3">
                  <SearchBar
                    value={searchQuery}
                    onChange={setSearchQuery}
                    placeholder="Search account, token, facility..."
                    className="w-full sm:w-64"
                  />
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    {filteredPositions.length} result
                    {filteredPositions.length !== 1 ? "s" : ""}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {filteredPositions.length === 0 ? (
            <div className="text-center py-12 text-sm text-muted-foreground">
              No positions match your filters.
            </div>
          ) : (
            <PermissionlessTable
              variant="verified"
              positions={paginatedPositions}
              onLiquidate={(pos) =>
                navigate(
                  `/liquidations/execute/${pos.accountId}?mode=verified-buyer`,
                )
              }
              actionLabel="Liquidate"
              sortKey={sortKey}
              sortDirection={sortDirection}
              onSort={onSort}
            />
          )}

          <PaginationControls
            page={permPage}
            setPage={setPermPage}
            pageSize={permPageSize}
            setPageSize={setPermPageSize}
            totalItems={filteredPositions.length}
            label="positions"
            pageSizeOptions={TABLE_PAGE_SIZE_OPTIONS}
          />
        </div>
      )}

      {/* ─── DDF Auctions Tab ────────────────────────────────────────────── */}
      {activeTab === "ddf" && <DdfAuctionsBrowse embedded />}

      {/* ─── History Tab ─────────────────────────────────────────────────── */}
      {activeTab === "history" && (
        <HistorySection
          events={paginatedHistory}
          total={HISTORY_EVENTS.length}
          page={histPage}
          setPage={setHistPage}
          pageSize={histPageSize}
          setPageSize={setHistPageSize}
          showViewAll
          onViewAll={() => navigate("/liquidations/history")}
          sortKey={historySortKey}
          sortDirection={historySortDirection}
          onSort={onSortHistory}
        />
      )}
    </div>
  );
}

// ─── Public (Unauthenticated) View ──────────────────────────────────────────────

function PublicLiquidations() {
  const navigate = useNavigate();
  const { connect } = useWalletStore();
  const { activeTab, setActiveTab } = useLiquidationsTabs();
  const [sortKey, setSortKey] = useState<PositionSortKey>("profitMargin");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [tokenFilter, setTokenFilter] = useState<TokenFilter>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [historySortKey, setHistorySortKey] = useState<HistorySortKey>("date");
  const [historySortDirection, setHistorySortDirection] =
    useState<SortDirection>("desc");

  const [permPage, setPermPage] = useState(1);
  const [permPageSize, setPermPageSize] = useState<number>(
    TABLE_PAGE_SIZE_OPTIONS[0],
  );
  const [histPage, setHistPage] = useState(1);
  const [histPageSize, setHistPageSize] = useState<number>(
    TABLE_PAGE_SIZE_OPTIONS[0],
  );

  // Reset to page 1 when filters/sort change.
  const permFilterKey = `${searchQuery}|${tokenFilter}|${sortKey}|${sortDirection}`;
  const [prevPermFilterKey, setPrevPermFilterKey] = useState(permFilterKey);
  if (prevPermFilterKey !== permFilterKey) {
    setPrevPermFilterKey(permFilterKey);
    setPermPage(1);
  }
  const histFilterKey = `${historySortKey}|${historySortDirection}`;
  const [prevHistFilterKey, setPrevHistFilterKey] = useState(histFilterKey);
  if (prevHistFilterKey !== histFilterKey) {
    setPrevHistFilterKey(histFilterKey);
    setHistPage(1);
  }

  const filteredPositions = useMemo(() => {
    let items = [...POSITIONS];
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      items = items.filter(
        (p) =>
          p.accountId.toLowerCase().includes(q) ||
          p.token.toLowerCase().includes(q) ||
          p.facility.toLowerCase().includes(q),
      );
    }
    if (tokenFilter !== "all")
      items = items.filter((p) => p.token === tokenFilter);
    items.sort((a, b) => {
      let comparison = 0;
      switch (sortKey) {
        case "accountId":
          comparison = compareText(a.accountId, b.accountId);
          break;
        case "facility":
          comparison = compareText(a.facility, b.facility);
          break;
        case "token":
          comparison = compareText(a.token, b.token);
          break;
        case "collateralAmount":
          comparison = a.collateralAmount - b.collateralAmount;
          break;
        case "collateralValue":
          comparison = a.collateralValue - b.collateralValue;
          break;
        case "outstandingDebt":
          comparison = a.outstandingDebt - b.outstandingDebt;
          break;
        case "healthFactor":
          comparison = a.healthFactor - b.healthFactor;
          break;
        case "timeInDefaultMinutes":
          comparison = a.timeInDefaultMinutes - b.timeInDefaultMinutes;
          break;
        case "findersFee":
          comparison = a.findersFee - b.findersFee;
          break;
        case "profitAmount":
          comparison = a.profitAmount - b.profitAmount;
          break;
        case "profitMargin":
          comparison = a.profitMargin - b.profitMargin;
          break;
      }
      return sortDirection === "asc" ? comparison : -comparison;
    });
    return items;
  }, [sortKey, sortDirection, tokenFilter, searchQuery]);

  const paginatedPositions = useMemo(() => {
    const start = (permPage - 1) * permPageSize;
    return filteredPositions.slice(start, start + permPageSize);
  }, [filteredPositions, permPage, permPageSize]);

  const sortedHistory = useMemo(() => {
    const items = [...HISTORY_EVENTS];
    items.sort((a, b) => {
      let comparison = 0;
      switch (historySortKey) {
        case "date":
          comparison =
            parseDateToTimestamp(a.date) - parseDateToTimestamp(b.date);
          break;
        case "type":
          comparison = compareText(a.type, b.type);
          break;
        case "accountId":
          comparison = compareText(a.accountId, b.accountId);
          break;
        case "token":
          comparison = compareText(a.token, b.token);
          break;
        case "collateralAmount":
          comparison = a.collateralAmount - b.collateralAmount;
          break;
        case "debtCovered":
          comparison = a.debtCovered - b.debtCovered;
          break;
        case "recoveryRate":
          comparison = a.recoveryRate - b.recoveryRate;
          break;
        case "status":
          comparison = compareText(a.status, b.status);
          break;
      }
      return historySortDirection === "asc" ? comparison : -comparison;
    });
    return items;
  }, [historySortDirection, historySortKey]);

  const paginatedHistory = useMemo(() => {
    const start = (histPage - 1) * histPageSize;
    return sortedHistory.slice(start, start + histPageSize);
  }, [histPage, histPageSize, sortedHistory]);

  function onSort(nextKey: PositionSortKey) {
    setSortDirection((prev) =>
      sortKey === nextKey ? (prev === "asc" ? "desc" : "asc") : "desc",
    );
    setSortKey(nextKey);
  }

  function onSortHistory(nextKey: HistorySortKey) {
    setHistorySortDirection((prev) =>
      historySortKey === nextKey ? (prev === "asc" ? "desc" : "asc") : "desc",
    );
    setHistorySortKey(nextKey);
  }

  const totalCollateralAtRisk = POSITIONS.reduce(
    (s, p) => s + p.collateralValue,
    0,
  );
  const averageRecoveryRate =
    HISTORY_EVENTS.length > 0
      ? HISTORY_EVENTS.reduce((sum, event) => sum + event.recoveryRate, 0) /
        HISTORY_EVENTS.length
      : 0;

  const tabs: { key: SubTab; label: string; badge?: string }[] = [
    {
      key: "searcher",
      label: "Searcher Opportunities",
      badge: String(POSITIONS.length),
    },
    { key: "verified", label: "Verified Buyer Liquidations" },
    {
      key: "ddf",
      label: "DDF Auctions",
      badge: String(CLIENT_DDF_AUCTIONS.length),
    },
    { key: "history", label: "History" },
  ];

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Hero section */}
      <div className="rounded-2xl border border-border bg-gradient-to-br from-primary/5 via-background to-primary/5 p-8 sm:p-10">
        <div className="flex items-start gap-3">
          <Icon name="streamline:legal-justice-hammer-hammer-work-legal-mallet-office-company-gavel-justice-judge-arbitration-court" className="mt-1 h-7 w-7 text-foreground flex-shrink-0" />
          <div>
            <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">
              Liquidation Marketplace
            </h1>
            <p className="mt-2 max-w-2xl text-muted-foreground">
              Searchers earn DDF bounties; verified buyers liquidate in-facility
              when compliance passes (bypasses auctions); anyone can bid in DDF
              auctions.
            </p>
          </div>
        </div>
        <Button onClick={connect} className="mt-6">
          <Icon name="streamline:money-wallet-money-payment-finance-wallet" className="mr-2 h-4 w-4" />
          Connect Wallet to Participate
        </Button>
      </div>

      {/* Protocol-level stat cards */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4 lg:gap-4">
        <StatCard
          title="Total Liquidatable Positions"
          value={String(POSITIONS.length)}
          icon={<Icon name="streamline:target" className="h-5 w-5 text-muted-foreground" />}
        />
        <StatCard
          title="Total Collateral at Risk"
          value={fmtCurrency(totalCollateralAtRisk)}
          icon={<Icon name="streamline:money-safe-vault-saving-combo-payment-safe-money-combination-finance" className="h-5 w-5 text-muted-foreground" />}
        />
        <StatCard
          title="DDF Auctions Active"
          value={String(CLIENT_DDF_AUCTIONS.length)}
          icon={<Icon name="streamline:legal-justice-hammer-hammer-work-legal-mallet-office-company-gavel-justice-judge-arbitration-court" className="h-5 w-5 text-muted-foreground" />}
        />
        <StatCard
          title="Average Recovery Rate"
          value={`${averageRecoveryRate.toFixed(1)}%`}
          icon={<Icon name="streamline:heart-rate-pulse-graph" className="h-5 w-5 text-green-500" />}
          valueClassName="text-green-600"
        />
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-1 flex-wrap">
        {tabs.map((tab) =>
          tab.key === "verified" ? (
            <TooltipProvider key={tab.key}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    className="inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors bg-muted/50 text-muted-foreground opacity-50 cursor-not-allowed"
                    disabled
                  >
                    {tab.label}
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>This feature is supported at the protocol level but is not currently enabled on the platform.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                activeTab === tab.key
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted/50 text-muted-foreground hover:text-foreground hover:bg-muted"
              }`}
            >
              {tab.label}
              {tab.badge && (
                <span
                  className={`inline-flex items-center justify-center rounded-full px-2 py-0.5 text-xs font-semibold ${
                    activeTab === tab.key
                      ? "bg-primary-foreground/20 text-primary-foreground"
                      : "bg-primary/10 text-primary"
                  }`}
                >
                  {tab.badge}
                </span>
              )}
            </button>
          ),
        )}
      </div>

      {/* ─── Searcher Opportunities Tab ─────────────────────────────── */}
      {activeTab === "searcher" && (
        <>
          {/* Info banner */}
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 flex items-start gap-3">
            <Icon name="streamline:interface-alert-information-circle-information-frame-info-more-help-point-circle" className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-amber-800">
              <span className="font-medium">Searcher opportunities</span> are
              open to anyone. Trigger liquidations through the DDF contract and
              earn a bounty for helping maintain protocol health. The bounty depends on the facility&apos;s
              configuration and is{" "}
              <span className="font-medium">not always guaranteed</span>.
              Connect your wallet to participate.
            </div>
          </div>

          {/* Filters */}
          <Card>
            <CardContent className="p-4 sm:p-6">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-2">
                  <label className="text-xs font-medium text-muted-foreground whitespace-nowrap">
                    Token
                  </label>
                  <select
                    value={tokenFilter}
                    onChange={(e) => setTokenFilter(e.target.value)}
                    className={`${INPUT_CLASS} w-full sm:w-40`}
                  >
                    <option value="all">All Tokens</option>
                    {ALL_TOKENS.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center gap-3">
                  <SearchBar
                    value={searchQuery}
                    onChange={setSearchQuery}
                    placeholder="Search account, token, facility..."
                    className="w-full sm:w-64"
                  />
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    {filteredPositions.length} result
                    {filteredPositions.length !== 1 ? "s" : ""}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {filteredPositions.length === 0 ? (
            <div className="text-center py-12 text-sm text-muted-foreground">
              No liquidation opportunities match your filters.
            </div>
          ) : (
            <PermissionlessTable
              positions={paginatedPositions}
              onLiquidate={connect}
              actionLabel="Connect"
              actionDisabled={false}
              sortKey={sortKey}
              sortDirection={sortDirection}
              onSort={onSort}
            />
          )}

          <PaginationControls
            page={permPage}
            setPage={setPermPage}
            pageSize={permPageSize}
            setPageSize={setPermPageSize}
            totalItems={filteredPositions.length}
            label="positions"
            pageSizeOptions={TABLE_PAGE_SIZE_OPTIONS}
          />
        </>
      )}

      {/* ─── Verified Buyer Tab (Public) ─────────────────────────────────── */}
      {activeTab === "verified" && (
        <div className="space-y-6">
          <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 flex items-start gap-3">
            <Icon name="streamline:interface-alert-information-circle-information-frame-info-more-help-point-circle" className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-blue-800">
              <span className="font-medium">Verified buyer</span> is{" "}
              <span className="font-medium">per credit facility</span>: the same
              compliance you complete when borrowing (facility page). When
              checks pass for a facility, you can liquidate margin accounts
              there <span className="font-medium">directly</span>, bypassing DDF
              auctions.
            </div>
          </div>
          <div className="flex flex-col items-center gap-4 py-8 text-center max-w-lg mx-auto">
            <Icon name="streamline:money-wallet-money-payment-finance-wallet" className="h-10 w-10 text-muted-foreground" />
            <p className="text-muted-foreground text-sm">
              Connect your wallet, open a credit facility, and complete its
              compliance pre-check. Then return here to use the direct
              liquidation flow for that facility.
            </p>
            <div className="flex flex-wrap gap-2 justify-center">
              <Button onClick={connect}>
                <Icon name="streamline:money-wallet-money-payment-finance-wallet" className="mr-2 h-4 w-4" />
                Connect Wallet
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate("/credit-facilities")}
              >
                Credit facilities
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ─── DDF Auctions Tab ────────────────────────────────────────────── */}
      {activeTab === "ddf" && (
        <DdfAuctionsBrowse embedded connectToBid onConnect={connect} />
      )}

      {/* ─── History Tab ─────────────────────────────────────────────────── */}
      {activeTab === "history" && (
        <HistorySection
          events={paginatedHistory}
          total={HISTORY_EVENTS.length}
          page={histPage}
          setPage={setHistPage}
          pageSize={histPageSize}
          setPageSize={setHistPageSize}
          sortKey={historySortKey}
          sortDirection={historySortDirection}
          onSort={onSortHistory}
        />
      )}
    </div>
  );
}

// ─── History Section (shared) ───────────────────────────────────────────────────

function HistorySection({
  events,
  total,
  page,
  setPage,
  pageSize,
  setPageSize,
  showViewAll,
  onViewAll,
  sortKey,
  sortDirection,
  onSort,
}: {
  events: HistoryEvent[];
  total: number;
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  pageSize: number;
  setPageSize: React.Dispatch<React.SetStateAction<number>>;
  showViewAll?: boolean;
  onViewAll?: () => void;
  sortKey: HistorySortKey;
  sortDirection: SortDirection;
  onSort: (nextKey: HistorySortKey) => void;
}) {
  const historyColumns: DataTableColumn<HistoryEvent, HistorySortKey>[] = [
    {
      id: "date",
      label: "Date",
      sortable: true,
      sortKey: "date",
      renderCell: (event) => <span className="text-muted-foreground text-xs">{event.date}</span>,
    },
    {
      id: "type",
      label: "Type",
      sortable: true,
      sortKey: "type",
      renderCell: (event) => (
        <span
          className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
            event.type === "DDF" ? "bg-purple-100 text-purple-700" : "bg-blue-100 text-blue-700"
          }`}
        >
          {event.type}
        </span>
      ),
    },
    {
      id: "accountId",
      label: "Account",
      sortable: true,
      sortKey: "accountId",
      renderCell: (event) => <span className="font-mono text-xs">{event.accountId}</span>,
    },
    {
      id: "collateralAmount",
      label: "Collateral Sold",
      sortable: true,
      sortKey: "collateralAmount",
      renderCell: (event) => (
        <div className="flex items-center gap-1.5">
          <TokenBadge token={event.token} colorClass={event.tokenColor} />
          <span className="text-xs">{event.collateralAmount.toFixed(2)}</span>
        </div>
      ),
    },
    {
      id: "debtCovered",
      label: "Debt Covered",
      align: "right",
      sortable: true,
      sortKey: "debtCovered",
      renderCell: (event) => fmtCurrency(event.debtCovered),
    },
    {
      id: "recoveryRate",
      label: "Recovery",
      align: "right",
      sortable: true,
      sortKey: "recoveryRate",
      renderCell: (event) => (
        <span
          className={`font-medium ${
            event.recoveryRate >= 95
              ? "text-green-600"
              : event.recoveryRate >= 90
                ? "text-amber-600"
                : "text-red-600"
          }`}
        >
          {event.recoveryRate.toFixed(1)}%
        </span>
      ),
    },
    {
      id: "status",
      label: "Status",
      align: "center",
      sortable: true,
      sortKey: "status",
      renderCell: (event) => (
        <div className="flex justify-center">
          <span
            className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${
              event.status === "Settled" ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"
            }`}
          >
            {event.status === "Settled" ? <Icon name="streamline:interface-validation-check-circle-checkmark-addition-circle-success-check-validation-add-form" className="h-3 w-3" /> : <Icon name="streamline:interface-time-clock-circle-clock-loading-measure-time-circle" className="h-3 w-3" />}
            {event.status}
          </span>
        </div>
      ),
    },
  ];

  return (
    <Card>
      <CardHeader className="p-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle>Recent Liquidation Events</CardTitle>
            <CardDescription>{total} events</CardDescription>
          </div>
          {showViewAll && onViewAll && (
            <Button variant="outline" size="sm" onClick={onViewAll}>
              View Full History
              <Icon name="streamline:interface-arrows-button-down-arrow-down-keyboard" className="ml-1 h-3 w-3 -rotate-90" />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="p-6 pt-0">
        {/* Desktop table */}
        <div className="hidden md:block">
          <DataTable<HistoryEvent, HistorySortKey>
            columns={historyColumns}
            rows={events}
            rowKey={(event) => event.id}
            sort={{
              key: sortKey,
              direction: sortDirection,
              onSort,
            }}
            emptyMessage="No liquidation events available."
          />
        </div>

        {/* Mobile stacked cards */}
        <div className="md:hidden space-y-3">
          {events.map((event) => (
            <div
              key={event.id}
              className="rounded-lg border border-border p-4 space-y-2"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span
                    className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
                      event.type === "DDF"
                        ? "bg-purple-100 text-purple-700"
                        : "bg-blue-100 text-blue-700"
                    }`}
                  >
                    {event.type}
                  </span>
                  <span className="font-mono text-xs">{event.accountId}</span>
                </div>
                <span
                  className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${
                    event.status === "Settled"
                      ? "bg-green-100 text-green-700"
                      : "bg-yellow-100 text-yellow-700"
                  }`}
                >
                  {event.status}
                </span>
              </div>
              <div className="text-xs text-muted-foreground">{event.date}</div>
              <div className="grid grid-cols-3 gap-2 text-xs">
                <div>
                  <span className="text-muted-foreground">Collateral</span>
                  <div className="flex items-center gap-1 mt-0.5">
                    <TokenBadge
                      token={event.token}
                      colorClass={event.tokenColor}
                    />
                    <span className="font-medium">
                      {event.collateralAmount.toFixed(2)}
                    </span>
                  </div>
                </div>
                <div>
                  <span className="text-muted-foreground">Debt Covered</span>
                  <div className="font-medium">
                    {fmtCurrency(event.debtCovered)}
                  </div>
                </div>
                <div className="text-right">
                  <span className="text-muted-foreground">Recovery</span>
                  <div
                    className={`font-medium ${event.recoveryRate >= 95 ? "text-green-600" : event.recoveryRate >= 90 ? "text-amber-600" : "text-red-600"}`}
                  >
                    {event.recoveryRate.toFixed(1)}%
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <PaginationControls
          page={page}
          setPage={setPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          totalItems={total}
          label="events"
          pageSizeOptions={TABLE_PAGE_SIZE_OPTIONS}
        />
      </CardContent>
    </Card>
  );
}

// ─── Main Export ─────────────────────────────────────────────────────────────────

export function LiquidationsPage() {
  const { isConnected } = useWalletStore();
  return isConnected ? <AuthenticatedLiquidations /> : <PublicLiquidations />;
}
